<template>
  <div class="basket-rule-requirement" :class="`--mode-${mode}`">
    <!-- КОМПАКТНЫЙ РЕЖИМ (для ProductCardLine, ProductCardSlide) -->
    <template v-if="mode === 'compact'">
      <!-- Текстовое описание требования -->
      <div
          v-if="requirementText"
          class="requirement-text s-font-2xs text-grey-7"
      >
        {{ requirementText }}
      </div>

      <!-- Прогресс бар для правил с ограничением по сумме -->
      <div v-if="showProgress" class="requirement-progress q-mt-xs">
        <div class="progress-bar-container">
          <div class="progress-bar-bg">
            <div
                class="progress-bar-fill"
                :class="progressColorClass"
                :style="{ width: `${progressPercent}%` }"
            />
          </div>
        </div>
        <div class="progress-text s-font-3xs text-grey-7 q-mt-xs">
          При заказе от <span class="text-no-wrap">{{ formatPrice(minOrderPrice) }} ₽</span>
        </div>
      </div>
    </template>

    <!-- ПОДРОБНЫЙ РЕЖИМ (для ProductCardGrid) -->
    <template v-else-if="mode === 'detailed'">
      <div
          class="detailed-requirement q-mb-md q-pa-sm bg-grey-2 rounded-borders"
      >
        <div class="text-caption text-grey-8 q-mb-xs">
          Доступно по акции:
        </div>

        <!-- Правила корзины -->
        <div
            v-for="ruleState in ruleStates"
            :key="ruleState.rule.ID"
            class="rule-item"
        >
          <!-- Если правило активно -->
          <div
              v-if="ruleState.active"
              class="text-caption text-weight-medium text-positive"
          >
            <q-icon :name="ICONS.checkCircle" size="14px" class="q-mr-xs"/>
            {{ ruleState.rule.NAME }}
            <span v-if="ruleState.rule.CONDITION?.ORDER_PRICE_FROM">
              (от {{ formatPrice(ruleState.rule.CONDITION.ORDER_PRICE_FROM) }} ₽)
            </span>
          </div>

          <!-- Если правило неактивно - показываем прогресс -->
          <div v-else class="q-mt-xs">
            <div class="text-caption text-weight-medium text-grey-7 q-mb-xs">
              <q-icon
                  :name="ICONS.radioButtonUnchecked"
                  size="14px"
                  class="q-mr-xs"
              />
              {{ ruleState.rule.NAME }}
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar-bg">
                <div
                    class="progress-bar-fill"
                    :class="getProgressColorClass(ruleState.progress * 100)"
                    :style="{ width: `${ruleState.progress * 100}%` }"
                />
              </div>
            </div>
            <div class="progress-text s-font-3xs text-grey-7 q-mt-xs">
              При заказе от
              {{ formatPrice(ruleState.rule.CONDITION?.ORDER_PRICE_FROM) }}
              ₽
              <span v-if="ruleState.priceRemaining > 0" class="text-grey-6">
                (осталось {{ formatPrice(ruleState.priceRemaining) }} ₽)
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {BasketRuleState} from "@/modules/shop/store/basket-rules";
import {computed} from "vue";
import {ICONS} from "@/assets/icons";

const props = withDefaults(
    defineProps<{
      mode?: "compact" | "detailed"; // Режим отображения
      // Для компактного режима
      ruleName?: string;
      priceRemaining?: number;
      progressPercent?: number;
      minOrderPrice?: number;
      isAvailable?: boolean;
      // Для подробного режима
      ruleStates?: BasketRuleState[];
    }>(),
    {
      mode: "compact",
      isAvailable: false,
      ruleStates: () => [],
    }
);

const showProgress = computed(() => {
  return (
      props.priceRemaining !== undefined &&
      props.progressPercent !== undefined &&
      !props.isAvailable
  );
});

const requirementText = computed(() => {
  if (props.isAvailable) {
    return null;
  }

  if (props.priceRemaining !== undefined) {
    // Для правил с ограничением по сумме текст будет в progress-text
    return null;
  }

  // Для правил без ограничения по сумме
  if (props.ruleName) {
    return `Требуется: ${props.ruleName}`;
  }

  return "Доступно по акции";
});

const formatPrice = (price: number | undefined | null): string => {
  if (price === undefined || price === null) return "0";
  return Math.ceil(price).toString();
};

const minOrderPrice = computed(() => {
  return props.minOrderPrice;
});

// Определяем цвет прогресс бара в зависимости от процента (для компактного режима)
const progressColorClass = computed(() => {
  return getProgressColorClass(props.progressPercent || 0);
});

// Функция для определения цвета прогресс бара (используется в обоих режимах)
const getProgressColorClass = (percent: number | undefined): string => {
  const p = percent || 0;

  if (p < 40) {
    return "progress-low"; // Серый (0-40%)
  } else if (p < 80) {
    return "progress-medium"; // Желтый (40-80%)
  } else {
    return "progress-high"; // Зеленый (80-100%)
  }
};
</script>

<style scoped lang="scss">
.basket-rule-requirement {
  margin-top: 3px;
}

.requirement-text {
  line-height: 1.2;
}

.progress-bar-container {
  width: 100%;
}

.progress-bar-bg {
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  transition: width 0.3s ease, background 0.3s ease;

  // Серый (0-40%)
  &.progress-low {
    background: linear-gradient(90deg, #9e9e9e 0%, #bdbdbd 100%);
  }

  // Желтый (40-80%)
  &.progress-medium {
    background: linear-gradient(90deg, #ffa726 0%, #ffb74d 100%);
  }

  // Зеленый (80-100%)
  &.progress-high {
    background: linear-gradient(90deg, #66bb6a 0%, #81c784 100%);
  }
}

.progress-text {
  line-height: 1.1;
  text-align: left;
}

// Стили для подробного режима
.--mode-detailed {
  .detailed-requirement {
    .rule-item {
      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }
}
</style>
